import { RESOURCES } from "../data/content";
import { isMediaPreview } from "./MediaSlot";
import { GuideGate } from "./GuideGate";

// Downloadable resource cards (guides, checklists, playbooks).
// Public mode: renders ONLY for resources with status "published". Approved
// guides are CAPTURE-GATED (GuideGate): the lead is saved server-side before
// a short-lived download token is issued — no open static PDF URLs.
// Owner preview (?media-preview=1 on non-production hosts) shows labelled
// placement frames for everything else — no fake links, no coming-soon.
const RESOURCE_STATUS_LABEL = {
  unpublished: "Awaiting approved file",
  draft: "Draft in editorial review — not published",
  hold: "Full edition — release pending (master retained privately)",
};

export const ResourceCard = ({ resource, className = "", sourcePage }) => {
  const published = resource.status === "published";

  if (published) {
    const meta = [
      resource.pageCount ? `${resource.pageCount} pages` : null,
      resource.fileSize || null,
      resource.reviewedAt ? `Updated ${resource.reviewedAt}` : null,
    ].filter(Boolean);
    return (
      <div className={`border border-charcoal/15 bg-ivory p-7 lg:p-8 ${className}`} data-testid={`resource-card-${resource.id}`}>
        <div className="flex items-start gap-5">
          {resource.coverUrl && (
            <img
              src={resource.coverUrl}
              alt={`${resource.title} cover`}
              loading="lazy"
              className="w-20 shrink-0 border border-charcoal/15 shadow-[0_2px_10px_rgba(26,26,26,0.08)]"
            />
          )}
          <div>
            <p className="text-[11px] font-semibold tracking-[0.24em] uppercase text-golddark">Free guide</p>
            <h3 className="mt-2 font-serif text-xl font-medium text-charcoal">{resource.title}</h3>
            <p className="mt-2 text-sm leading-relaxed text-charcoal/65">{resource.summary}</p>
            {resource.formatNote && (
              <p className="mt-2 text-xs text-charcoal/50">{resource.formatNote}</p>
            )}
          </div>
        </div>
        <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2">
          {meta.length > 0 && (
            <span className="text-xs text-charcoal/45">{meta.join(" · ")}</span>
          )}
        </div>
        {resource.gated ? (
          <GuideGate
            guideId={resource.id}
            guideTitle={resource.title}
            sourcePage={sourcePage}
            testid={`resource-gate-${resource.id}`}
          />
        ) : (
          resource.fileUrl && (
            <div className="mt-5">
              <a
                href={resource.fileUrl}
                download
                data-testid={`resource-download-${resource.id}`}
                className="inline-flex items-center gap-2 border border-charcoal px-5 py-2.5 text-[11px] font-semibold tracking-[0.2em] uppercase text-charcoal transition-[background-color,color] duration-300 hover:bg-charcoal hover:text-gold"
              >
                Download PDF <span aria-hidden="true">↓</span>
              </a>
            </div>
          )
        )}
      </div>
    );
  }

  if (isMediaPreview()) {
    const meta = [
      resource.pageCount ? `${resource.pageCount} pages` : resource.expectedPageCount ? `${resource.expectedPageCount} pages` : null,
      resource.fileSize || null,
      resource.expectedFileName || null,
    ].filter(Boolean);
    return (
      <div
        className={`border border-dashed border-gold/70 bg-gold/5 p-7 lg:p-8 ${className}`}
        data-testid={`resource-card-${resource.id}`}
        data-preview-frame="true"
        role="note"
        aria-label={`Resource placement — ${resource.title}`}
      >
        <p className="text-[11px] font-semibold tracking-[0.24em] uppercase text-golddark">Download placement</p>
        <h3 className="mt-2 font-serif text-xl text-charcoal/85">{resource.title}</h3>
        <p className="mt-2 text-xs leading-relaxed text-charcoal/55">{resource.summary}</p>
        <p className="mt-3 text-[11px] tracking-[0.14em] uppercase text-charcoal/45">
          {RESOURCE_STATUS_LABEL[resource.status] || "Not published"}
          {meta.length > 0 ? ` — ${meta.join(" · ")}` : ""}
        </p>
      </div>
    );
  }

  return null;
};

// Renders every resource for a given service ("buyer" | "vendor" | "insights").
// Nothing renders publicly until a resource is published with a real fileUrl.
export const ResourceList = ({ service, className = "", sourcePage }) => {
  const items = RESOURCES.filter((r) => r.service === service);
  const visible = items.filter(
    (r) => r.status === "published" || isMediaPreview()
  );
  if (visible.length === 0) return null;
  return (
    <div id="guides" className={`scroll-mt-28 ${className}`} data-testid={`resource-list-${service}`}>
      <p className="text-xs font-semibold tracking-[0.3em] uppercase text-golddark">Free guides</p>
      <div className="mt-5 space-y-5">
        {visible.map((r) => (
          <ResourceCard key={r.id} resource={r} sourcePage={sourcePage || `/${service}`} />
        ))}
      </div>
    </div>
  );
};
